import { useEffect } from 'react';
import type { MutableRefObject } from 'react';
import type GoogleMapReact from 'google-map-react';

import { recenterOnMarker } from './helpers';

export const useDinamicMapUiEffects = ({
    debouncedSearch,
    appliedFilters,
    appliedFiltersRef,
    stationDetailsById,
    stationDetailsByIdRef,
    stationDetailsLoadingById,
    stationDetailsLoadingByIdRef,
    mapRef,
    mapsRef,
    searchPinLocation,
    searchPinMarkerRef,
    searchBlurTimeoutRef,
    directionsRendererRef,
    openMarker,
    stations,
    setCenter,
    setZoom,
    expandedSidebarStationId,
    baseSidebarStations,
    selectedSidebarStation,
    sidebarCardRefs,
    sidebarListRef,
    searchSuggestions,
    highlightedSuggestionIndex,
    setHighlightedSuggestionIndex,
    showSearchSuggestions,
    suggestionRefs,
    activeSidebarStation,
}: {
    debouncedSearch: { cancel: () => void };
    appliedFilters: any;
    appliedFiltersRef: MutableRefObject<any>;
    stationDetailsById: Record<string, any>;
    stationDetailsByIdRef: MutableRefObject<Record<string, any>>;
    stationDetailsLoadingById: Record<string, boolean>;
    stationDetailsLoadingByIdRef: MutableRefObject<Record<string, boolean>>;
    mapRef: MutableRefObject<google.maps.Map | undefined>;
    mapsRef: MutableRefObject<typeof google.maps | undefined>;
    searchPinLocation: GoogleMapReact.Coords | null;
    searchPinMarkerRef: MutableRefObject<google.maps.Marker | null>;
    searchBlurTimeoutRef: MutableRefObject<ReturnType<typeof setTimeout> | null>;
    directionsRendererRef: MutableRefObject<google.maps.DirectionsRenderer | null>;
    openMarker: string | number | null;
    stations: any[];
    setCenter: React.Dispatch<React.SetStateAction<GoogleMapReact.Coords>>;
    setZoom: React.Dispatch<React.SetStateAction<number>>;
    expandedSidebarStationId: string | number | null;
    baseSidebarStations: any[];
    selectedSidebarStation: any | null;
    sidebarCardRefs: MutableRefObject<Record<string, HTMLDivElement | null>>;
    sidebarListRef: MutableRefObject<HTMLDivElement | null>;
    searchSuggestions: any[];
    highlightedSuggestionIndex: number;
    setHighlightedSuggestionIndex: React.Dispatch<React.SetStateAction<number>>;
    showSearchSuggestions: boolean;
    suggestionRefs: MutableRefObject<Array<HTMLButtonElement | null>>;
    activeSidebarStation: any;
}) => {
    useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

    useEffect(() => {
        appliedFiltersRef.current = appliedFilters;
    }, [appliedFilters, appliedFiltersRef]);

    useEffect(() => {
        stationDetailsByIdRef.current = stationDetailsById;
    }, [stationDetailsById, stationDetailsByIdRef]);

    useEffect(() => {
        stationDetailsLoadingByIdRef.current = stationDetailsLoadingById;
    }, [stationDetailsLoadingById, stationDetailsLoadingByIdRef]);

    useEffect(() => {
        if (!mapRef.current || !mapsRef.current) {
            return;
        }

        if (!searchPinLocation) {
            searchPinMarkerRef.current?.setMap(null);
            searchPinMarkerRef.current = null;
            return;
        }

        if (!searchPinMarkerRef.current) {
            searchPinMarkerRef.current = new mapsRef.current.Marker({
                map: mapRef.current,
                position: searchPinLocation,
            });
            return;
        }

        searchPinMarkerRef.current.setMap(mapRef.current);
        searchPinMarkerRef.current.setPosition(searchPinLocation);
    }, [mapRef, mapsRef, searchPinLocation, searchPinMarkerRef]);

    useEffect(() => {
        return () => {
            if (searchBlurTimeoutRef.current) {
                clearTimeout(searchBlurTimeoutRef.current);
            }
            searchPinMarkerRef.current?.setMap(null);
            directionsRendererRef.current?.setMap(null);
        };
    }, [searchBlurTimeoutRef, searchPinMarkerRef, directionsRendererRef]);

    useEffect(() => {
        recenterOnMarker({
            openMarker,
            stations,
            setCenter,
            setZoom,
            map: mapRef.current,
        });
    }, [openMarker]);

    useEffect(() => {
        if (expandedSidebarStationId === null) return;

        const animationFrameId = window.requestAnimationFrame(() => {
            const activeCard = sidebarCardRefs.current[String(expandedSidebarStationId)];
            const sidebarList = sidebarListRef.current;

            if (!activeCard || !sidebarList) return;

            const nextScrollTop = activeCard.offsetTop - sidebarList.offsetTop - 12;

            sidebarList.scrollTo({
                top: Math.max(0, nextScrollTop),
                behavior: 'smooth',
            });
        });

        return () => window.cancelAnimationFrame(animationFrameId);
    }, [expandedSidebarStationId, baseSidebarStations, selectedSidebarStation, sidebarCardRefs, sidebarListRef]);

    useEffect(() => {
        if (highlightedSuggestionIndex >= searchSuggestions.length) {
            setHighlightedSuggestionIndex(searchSuggestions.length > 0 ? searchSuggestions.length - 1 : -1);
        }
    }, [searchSuggestions, highlightedSuggestionIndex, setHighlightedSuggestionIndex]);

    useEffect(() => {
        if (!showSearchSuggestions || highlightedSuggestionIndex < 0) return;
        const activeSuggestion = suggestionRefs.current[highlightedSuggestionIndex];
        activeSuggestion?.scrollIntoView({block: 'nearest'});
    }, [highlightedSuggestionIndex, showSearchSuggestions, suggestionRefs]);

    useEffect(() => {
        const isStationModalOpen = !!activeSidebarStation?.properties?.data;

        if (!isStationModalOpen || !window.matchMedia('(max-width: 991px)').matches) {
            return;
        }

        const scrollY = window.scrollY;
        const htmlElement = document.documentElement;
        const {style: bodyStyle} = document.body;
        const {style: htmlStyle} = htmlElement;
        const previousOverflow = bodyStyle.overflow;
        const previousPosition = bodyStyle.position;
        const previousTop = bodyStyle.top;
        const previousWidth = bodyStyle.width;
        const previousLeft = bodyStyle.left;
        const previousRight = bodyStyle.right;
        const previousHtmlOverflow = htmlStyle.overflow;

        bodyStyle.overflow = 'hidden';
        bodyStyle.position = 'fixed';
        bodyStyle.top = `-${scrollY}px`;
        bodyStyle.left = '0';
        bodyStyle.right = '0';
        bodyStyle.width = '100%';
        htmlStyle.overflow = 'hidden';

        return () => {
            bodyStyle.overflow = previousOverflow;
            bodyStyle.position = previousPosition;
            bodyStyle.top = previousTop;
            bodyStyle.width = previousWidth;
            bodyStyle.left = previousLeft;
            bodyStyle.right = previousRight;
            htmlStyle.overflow = previousHtmlOverflow;
            window.scrollTo(0, scrollY);
        };
    }, [activeSidebarStation]);
};
